import { Box, TextField, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { ColorScheme } from "../../../Themes/ColorScheme";
import { ApiContext } from "../../ApiContext";
import { Spinner } from "../../Common/Spinner";
import { KeyUserList } from "./KeyUserList";
import { SortingOptions } from "./SortingOptions";

import { useTranslation } from "react-i18next";
import PagingIndicator from "../../Common/PagingIndicator";
import SelectionActionBar from "./SelectionActionBar";

const ONE_PAGE_LIMTI = 20;

export const KeyAccessList = ({ data, handleRefresh, refreshNeeded, setRefreshNeeded }) => {
	const { ApiCall } = useContext(ApiContext);
	const { t } = useTranslation();

	const [search, setSearch] = useState("");

	const [page, setPage] = useState(1);

	const [loading, setLoading] = useState(true);
	const [content, setContent] = useState([]);
	const [boxHeight, setBoxHeight] = useState(80);

	const [selectedUsers, setSelectedUsers] = useState([]);

	const [readyToFetch, setReadyToFetch] = useState(false);

	const [sort, setSort] = useState("user_name ASC");

	function fetchUserData() {
		setLoading(true);
		setContent([]);
		ApiCall(
			"GET",
			`client/residents/${data.id}?search=${search}&page=${page}&sort=${sort}`
		)
			.then((res) => {
				setContent(res);
				setReadyToFetch(false);
				setLoading(false);
				setRefreshNeeded(false);
			})
			.catch(() => {
				// console.log(error);
				setLoading(false);
			});
	}

	const handleSelectAll = (checked) => {
		if (checked) {
			const allUsers = content.map((user) => user);
			setSelectedUsers(allUsers);
		} else {
			setSelectedUsers([]);
		}
	}

	const handlePageChange = (newPage) => {
		if (loading) return; // prevent changing page while loading
		setPage(newPage);
	}

	const handleSelectedUsersClear = () => {
		setSelectedUsers([]);
	}

	useEffect(() => {
		if (readyToFetch) {
			fetchUserData();
		}
	}, [readyToFetch]);

	const handleChange = (text) => {
		const onlyLowerCase = text.target.value.toLowerCase();
		setSearch(onlyLowerCase);
	};

	useEffect(() => {
		setSelectedUsers([]);
		if (page == null) {
			setPage(1);
			return;
		}
		setPage(null);
	}, [search, data, sort]);

	useEffect(() => {
		setSelectedUsers([]);
		if (page === null) {
			setPage(1);
			return;
		}
		setLoading(true);
		setContent([]);
		const delay = 500;
		const delayDebounceFn = setTimeout(() => {
			setReadyToFetch(true);
		}, delay);

		return () => clearTimeout(delayDebounceFn);
	}, [page]);

	useEffect(() => {
		const boxHeightT =
			81 * content.length +
			2 +
			(data.user_count != 0 && search == "" && 80) // Paging selector height
			+ (loading ? 80 : 0) // loading indicator space
			+ (!loading && content.length === 0 ? 80 : 0); // empty state space
		setBoxHeight(boxHeightT);
	}, [loading, content]);

	useEffect(() => {
		if (refreshNeeded) {
			fetchUserData();
		}
	}, [refreshNeeded]);

	return (
		<div style={{ position: "relative" }}>
			<div
				style={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					justifyContent: "space-between",
					marginBottom: "20px"
				}}
			>
				<Typography fontSize="24px">{t("key_management.access_rights")}</Typography>
				<Typography fontSize="20px">
					{data.user_count || "0"} / {data.user_limit}
				</Typography>
			</div>
			<TextField
				fullWidth
				variant="filled"
				label={t("textfield_labels.search")}
				InputLabelProps={{ style: { color: ColorScheme.darkGrey } }}
				inputProps={{
					style: {
						color: ColorScheme.black,
						backgroundColor: ColorScheme.secondary,
						borderRadius: "4px 4px 0px 0px",
						border: "0px"
					}
				}}
				onChange={handleChange}
			/>

			<SortingOptions sort={sort} setSort={setSort} selectAllPressed={handleSelectAll} allSelected={content.length != 0 && selectedUsers.length == content.length} />
			<Box
				sx={{
					height: boxHeight + "px",
					backgroundColor: ColorScheme.secondary,
					alignItems: "center",
					justifyContent: "center",
					display: "flex",
					borderBottomLeftRadius: "4px",
					borderBottomRightRadius: "4px",
					width: "100%",
					transition: "all .2s ease-out",
					overflow: "hidden",
					flexDirection: "column"
				}}
			>
				{content.length === 0 && !loading && (
					<Typography sx={{ color: ColorScheme.black }}>
						{search === ""
							? t("key.no_users_yet")
							: t("key.no_users_found")}
					</Typography>
				)}
				<div
					style={{
						backgroundColor: ColorScheme.secondary,
						borderBottomLeftRadius: "4px",
						borderBottomRightRadius: "4px",
						top: "100px",
						width: "100%"
					}}
				>
					{content.map((entry, i) => (
						<KeyUserList
							handleRefresh={handleRefresh}
							reservable={data}
							key={i}
							entry={entry}
							i={i}
							isLast={i === content.length - 1}
							selectedUsers={selectedUsers}
							setSelectedUsers={setSelectedUsers}
						/>
					))}
				</div>
				{loading && <div style={{width: "100%", height: "80px", display: "flex", justifyContent: "center", alignItems: "center", margin: "0"}}>
					<Spinner small color={ColorScheme.primary} />
				</div>}
				{data.user_count != 0 && search == "" && <div style={{width: "100%", height: "80px", display: "flex", justifyContent: "center", alignItems: "center", margin: "0"}}>
					<PagingIndicator totalPages={Math.ceil(data.user_count/ONE_PAGE_LIMTI)} hideTotalPagesCount={false} currentPage={page} onPageChange={handlePageChange}/>
				</div>}
			</Box>
			<div style={{height: "50px"}}></div>
			{
				<SelectionActionBar handleRefresh={handleRefresh} reservable={data} selectedUsers={selectedUsers} clearSelectedUsers={handleSelectedUsersClear}/>
			}
		</div>
	);
};
